import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

const SSE_CLIENT_HTML = `<!-- Save as: client.html (in same folder as server.mjs) -->
<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>onefold SSE Notifications</title></head>
<body>
<div id="app"></div>
<script type="module">
import { createEventSource, html, mount } from 'https://esm.sh/onefold@latest';

const feed = createEventSource('/api/notifications');

function App() {
  return html\`
    <div style="max-width:500px;margin:20px auto;font-family:sans-serif">
      <h2>Live Notifications (\${() => feed.status()})</h2>
      <div style="padding:10px;background:#f0fdf4;border-radius:8px;margin-bottom:12px">
        Latest: <strong>\${() => feed.latest()?.message ?? 'Waiting...'}</strong>
      </div>
      <div style="max-height:400px;overflow-y:auto">
        \${() => feed.data().map(n => html\`
          <div style="padding:8px;margin-bottom:4px;border-left:3px solid #4338CA;background:#f8f9fb;border-radius:0 6px 6px 0">
            \${n.message}
            <small style="color:#999;margin-left:8px">\${new Date(n.timestamp).toLocaleTimeString()}</small>
          </div>
        \`)}
      </div>
      <button onclick=\${() => feed.close()} style="margin-top:12px;padding:6px 12px">Disconnect</button>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));
<\/script>
</body></html>`;

export function SsePage(): Node {
  return html`
    <div>
      <h1>Server-Sent Events (SSE)</h1>
      <p><code>createEventSource</code> wraps the native EventSource API in reactive signals. One-way server push — the server sends events to the client over a persistent HTTP connection.</p>

      <h2>Client Usage</h2>
      ${CodeBlock(`import { html, mount } from 'onefold';
import { createEventSource } from 'onefold/stream';

interface Notification {
  id: string;
  message: string;
  type: string;
  timestamp: number;
}

const feed = createEventSource<Notification>('/api/notifications');

// Reactive signals — UI updates when events arrive
feed.data()      // Signal<Notification[]> — all received events
feed.latest()    // Signal<Notification | null> — most recent event
feed.status()    // Signal<'connecting' | 'open' | 'closed' | 'error'>

// Close the connection
feed.close();`)}

      <h2>Live Notifications Example</h2>
      ${CodeBlock(`import { html, mount } from 'onefold';
import { createEventSource } from 'onefold/stream';

interface Notification { id: string; message: string; type: string; timestamp: number; }

const notifications = createEventSource<Notification>('/api/notifications');

function NotificationPanel() {
  return html\`
    <div>
      <h2>Notifications (\${() => notifications.status()})</h2>
      <p>Latest: <strong>\${() => notifications.latest()?.message ?? 'Waiting...'}</strong></p>

      <ul style="list-style:none;padding:0">
        \${() => notifications.data().map(n => html\`
          <li style="padding:8px;margin-bottom:4px;border-left:3px solid #4338CA;background:#f8f9fb;border-radius:0 6px 6px 0">
            <strong>\${n.message}</strong><br/>
            <small style="color:#64748b">\${new Date(n.timestamp).toLocaleTimeString()}</small>
          </li>
        \`)}
      </ul>

      <button onclick=\${() => notifications.close()}>Disconnect</button>
    </div>
  \`;
}

mount(NotificationPanel(), document.getElementById('app')!);`)}

      <h2>Server (Express.js)</h2>
      <p>SSE is just a long-lived HTTP response with <code>Content-Type: text/event-stream</code>:</p>
      ${CodeBlock(`// server.mjs
import express from 'express';

const app = express();
const clients = new Set();

// SSE endpoint
app.get('/api/notifications', (req, res) => {
  // Required headers for SSE
  res.writeHead(200, {
    'Content-Type': 'text/event-stream',
    'Cache-Control': 'no-cache',
    'Connection': 'keep-alive',
  });

  // Send initial event
  res.write(\`data: \${JSON.stringify({ id: '0', message: 'Connected', type: 'system', timestamp: Date.now() })}\\n\\n\`);

  clients.add(res);

  // Cleanup on client disconnect
  req.on('close', () => {
    clients.delete(res);
  });
});

// Push an event to all connected clients
function broadcast(event) {
  const data = JSON.stringify(event);
  for (const client of clients) {
    client.write(\`data: \${data}\\n\\n\`);
  }
}

// Example: push events when something happens
app.post('/api/orders', express.json(), (req, res) => {
  // Process order...
  const order = { id: Date.now(), ...req.body };

  // Notify all connected clients
  broadcast({
    id: String(order.id),
    message: \`New order #\${order.id} received\`,
    type: 'order',
    timestamp: Date.now(),
  });

  res.json({ success: true, orderId: order.id });
});

app.listen(3000, () => console.log('SSE server on :3000'));`)}

      <h2>Server (Node.js — no Express)</h2>
      ${CodeBlock(`// server.mjs — pure Node.js
import http from 'node:http';

const clients = new Set();

const server = http.createServer((req, res) => {
  if (req.url === '/api/events' && req.method === 'GET') {
    res.writeHead(200, {
      'Content-Type': 'text/event-stream',
      'Cache-Control': 'no-cache',
      'Connection': 'keep-alive',
    });

    res.write(\`data: \${JSON.stringify({ message: 'Connected' })}\\n\\n\`);
    clients.add(res);
    req.on('close', () => clients.delete(res));
    return;
  }

  res.writeHead(404);
  res.end('Not found');
});

// Push events periodically
setInterval(() => {
  const event = { message: 'Server tick', timestamp: Date.now() };
  for (const client of clients) {
    client.write(\`data: \${JSON.stringify(event)}\\n\\n\`);
  }
}, 3000);

server.listen(3000);`)}

      <h2>SSE Wire Format</h2>
      <p>The protocol is simple text. Each message is <code>data:</code> followed by the payload, terminated by two newlines:</p>
      ${CodeBlock(`// Single event
data: {"message":"Hello"}\\n\\n

// Event with ID (for reconnection)
id: 42\\n
data: {"message":"Hello"}\\n\\n

// Named event type
event: notification\\n
data: {"message":"New order"}\\n\\n

// Multi-line data
data: line 1\\n
data: line 2\\n\\n`)}

      ${Callout('The browser automatically reconnects SSE if the connection drops. If you send an id: field, the browser includes Last-Event-ID header on reconnect so the server can resume from where it left off.')}

      <h2>Authentication</h2>
      <p>Unlike WebSocket, SSE is a standard HTTP request — it sends cookies automatically and supports all HTTP auth mechanisms.</p>

      <h3>Option 1: Cookie-based (simplest)</h3>
      ${CodeBlock(`// Client — cookies are sent automatically, no extra code
const feed = createEventSource<Notification>('/api/notifications');

// Server (Express) — check session cookie
app.get('/api/notifications', (req, res) => {
  const session = req.cookies.session_id;
  if (!session || !validateSession(session)) {
    res.status(401).json({ error: 'Unauthorized' });
    return;
  }

  // Authenticated — proceed with SSE
  res.writeHead(200, {
    'Content-Type': 'text/event-stream',
    'Cache-Control': 'no-cache',
    'Connection': 'keep-alive',
  });
  clients.add(res);
  req.on('close', () => clients.delete(res));
});`)}

      <h3>Option 2: Token in URL</h3>
      ${CodeBlock(`// Client — pass token in query parameter
const token = getAuthToken();
const feed = createEventSource<Notification>(\`/api/notifications?token=\${token}\`);

// Server — validate token from query string
app.get('/api/notifications', (req, res) => {
  const token = req.query.token;
  if (!token || !verifyToken(token)) {
    res.status(401).json({ error: 'Unauthorized' });
    return;
  }

  const user = decodeToken(token);

  res.writeHead(200, { 'Content-Type': 'text/event-stream', ... });

  // Only send events this user is authorized to see
  clients.set(res, { userId: user.id, roles: user.roles });
  req.on('close', () => clients.delete(res));
});`)}

      ${Callout('SSE does NOT support custom headers in the browser (the EventSource API has no headers option). Use cookies or URL tokens for authentication.')}

      <h3>Authorization (per-event filtering)</h3>
      ${CodeBlock(`// Server — only send events the user is allowed to see
function broadcast(event, requiredRole) {
  for (const [client, meta] of clients) {
    // Skip clients without the required role
    if (requiredRole && !meta.roles.includes(requiredRole)) continue;
    client.write(\`data: \${JSON.stringify(event)}\\n\\n\`);
  }
}

// Only admins see this
broadcast({ message: 'Server restarting' }, 'admin');

// Everyone sees this
broadcast({ message: 'New product available' });`)}

      <h2>When to Use SSE vs WebSocket</h2>
      <table>
        <tr><th>Use SSE when...</th><th>Use WebSocket when...</th></tr>
        <tr><td>Server pushes data to client (one-way)</td><td>Client AND server exchange messages (two-way)</td></tr>
        <tr><td>Notifications, live feeds, dashboards</td><td>Chat, gaming, collaborative editing</td></tr>
        <tr><td>Works through CDNs and reverse proxies</td><td>May be blocked by some proxies</td></tr>
        <tr><td>Auto-reconnect built into browser</td><td>Need manual reconnect logic</td></tr>
        <tr><td>Standard HTTP (easy to debug, curl-friendly)</td><td>Separate protocol (harder to debug)</td></tr>
        <tr><td>Text only</td><td>Text and binary</td></tr>
      </table>

      <h2>Options</h2>
      <table>
        <tr><th>Option</th><th>Type</th><th>Default</th><th>Description</th></tr>
        <tr><td><code>maxEvents</code></td><td>number</td><td>100</td><td>Max events kept in data() array</td></tr>
        <tr><td><code>eventName</code></td><td>string</td><td>'message'</td><td>Event type to listen for</td></tr>
        <tr><td><code>parse</code></td><td>function</td><td>JSON.parse</td><td>Custom event data parser</td></tr>
      </table>

      <h2>Try It (Simulated SSE)</h2>
      <p>This playground simulates server-sent events so you can see how the reactive signals update the UI. Run the Express server above to connect for real.</p>

      ${Playground(`import { createSignal, html, mount } from 'onefold';

// Simulated SSE — demonstrates the reactive API pattern
// For a real app, use: createEventSource('/api/notifications')

const events = createSignal([]);
const latest = createSignal(null);
const status = createSignal('connecting');

// Simulate connection
setTimeout(() => {
  status.set('open');
  const welcome = { id: '0', message: 'Stream connected', type: 'system', timestamp: Date.now() };
  events.set([welcome]);
  latest.set(welcome);
}, 400);

// Simulate server pushing events every 2 seconds
const types = ['order', 'payment', 'shipping', 'review'];
const messages = [
  'New order #1042 received',
  'Payment of $49.99 processed',
  'Package shipped to NYC',
  'New 5-star review posted',
  'User signed up',
  'Subscription renewed',
  'Refund issued for #1038',
];

setInterval(() => {
  if (status() !== 'open') return;
  const event = {
    id: String(Date.now()),
    message: messages[Math.floor(Math.random() * messages.length)],
    type: types[Math.floor(Math.random() * types.length)],
    timestamp: Date.now(),
  };
  latest.set(event);
  events.set(prev => {
    const next = [...prev, event];
    return next.length > 20 ? next.slice(-20) : next;
  });
}, 2000);

function App() {
  return html\`
    <div>
      <h3>Live Notifications (\${() => status()})</h3>
      <div style="margin-bottom:8px;padding:8px;background:#f0fdf4;border-radius:6px;font-size:13px">
        Latest: <strong>\${() => latest()?.message ?? 'Waiting...'}</strong>
      </div>
      <div style="height:180px;overflow-y:auto;font-size:12px">
        \${() => events().map(n => html\`
          <div style="padding:6px 8px;margin-bottom:4px;border-left:3px solid \${n.type === 'order' ? '#4338CA' : n.type === 'payment' ? '#16a34a' : '#ca8a04'};background:#f8f9fb;border-radius:0 4px 4px 0">
            \${n.message}
            <small style="color:#64748b;margin-left:6px">\${new Date(n.timestamp).toLocaleTimeString()}</small>
          </div>
        \`)}
      </div>
      <button onclick=\${() => status.set('closed')} style="margin-top:8px">Disconnect</button>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'SSE Notifications (Simulated)')}

      <div class="callout">
        <p><strong>To connect to a real server:</strong> Replace the simulated signals with <code>createEventSource('/api/notifications')</code> and run the Express/Node.js server code above. The API (<code>.data()</code>, <code>.latest()</code>, <code>.status()</code>, <code>.close()</code>) is identical.</p>
      </div>

      <h2>Run Locally (Copy and Paste)</h2>
      <p>Save these two files, run the server, then open the client in a browser:</p>

      <h3>Step 1: server.mjs</h3>
      ${CodeBlock(`// Save as: server.mjs
// Run: node server.mjs
import http from 'node:http';
import { readFileSync } from 'node:fs';

const clients = new Set();

const server = http.createServer((req, res) => {
  if (req.url === '/') {
    res.writeHead(200, { 'Content-Type': 'text/html' });
    res.end(readFileSync('client.html'));
    return;
  }

  if (req.url === '/api/notifications') {
    res.writeHead(200, {
      'Content-Type': 'text/event-stream',
      'Cache-Control': 'no-cache',
      'Connection': 'keep-alive',
    });
    res.write(\`data: \${JSON.stringify({ id: '0', message: 'Connected!', type: 'system', timestamp: Date.now() })}\\n\\n\`);
    clients.add(res);
    req.on('close', () => clients.delete(res));
    return;
  }

  res.writeHead(404); res.end();
});

// Push a random event every 2 seconds
const messages = ['New order received', 'Payment processed', 'User signed up', 'Review posted', 'Item shipped'];
setInterval(() => {
  const event = {
    id: String(Date.now()),
    message: messages[Math.floor(Math.random() * messages.length)],
    type: 'notification',
    timestamp: Date.now(),
  };
  for (const client of clients) {
    client.write(\`data: \${JSON.stringify(event)}\\n\\n\`);
  }
}, 2000);

server.listen(3000, () => console.log('SSE server: http://localhost:3000'));`)}

      <h3>Step 2: client.html</h3>
      ${CodeBlock(SSE_CLIENT_HTML)}

      <h3>Step 3: Run</h3>
      ${CodeBlock(`node server.mjs
# Open http://localhost:3000 — events appear every 2 seconds
# Open in multiple tabs — all receive the same events`)}

      <h2>Testing SSE with curl</h2>
      ${CodeBlock(`# Connect and see events stream in real-time
curl -N http://localhost:3000/api/notifications

# With auth token
curl -N "http://localhost:3000/api/notifications?token=eyJhbG..."

# With cookie
curl -N -b "session_id=abc123" http://localhost:3000/api/notifications`)}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/streaming/websocket">WebSocket</a> — for bidirectional communication</li>
        <li><a href="/data/http-client">HTTP Client</a> — for request/response APIs</li>
        <li><a href="/data/interceptors">Interceptors</a> — add auth headers to HTTP requests</li>
      </ul>
    </div>
  `;
}
